import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Link,
    Avatar,
    styled
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

interface ProfilePhotoProps {
    initialName?: string;
    initialPhotoUrl?: string;
    onPhotoChange?: (file: File) => void;
    onPhotoDelete?: () => void;
}

const UploadBox = styled(Box)(({ theme }) => ({
    border: '1px dashed #ccc',
    borderRadius: '8px',
    padding: theme.spacing(4),
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
        borderColor: theme.palette.primary.main,
    }
}));

const ProfilePhoto: React.FC<ProfilePhotoProps> = ({
    initialName = 'John Doe',
    initialPhotoUrl = '',
    onPhotoChange = (file) => console.log('Nuevo archivo:', file),
    onPhotoDelete = () => console.log('Foto borrada')
}) => {
    const [name] = useState(initialName);
    const [photoUrl, setPhotoUrl] = useState(initialPhotoUrl);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setPhotoUrl(imageUrl);
            onPhotoChange(file);
        }
    };

    const handleDeletePhoto = () => {
        setPhotoUrl('');
        onPhotoDelete();
    };

    const handleEditClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const imageUrl = URL.createObjectURL(file);
            setPhotoUrl(imageUrl);
            onPhotoChange(file);
        }
    };

    const handleBoxClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                p: 4,
                border: '1px solid #eaeaea',
                maxWidth: '100%'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                    src={photoUrl}
                    alt={name}
                    sx={{
                        width: 150,
                        height: 150,
                        mr: 3,
                        border: '1px solid #eaeaea'
                    }}
                />

                <Box>
                    <Typography variant="h4" component="h1" fontWeight={500} gutterBottom>
                        {name}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Link
                            component="button"
                            onClick={handleDeletePhoto}
                            underline="none"
                            color="inherit"
                            sx={{
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                color: 'text.secondary',
                            }}
                        >
                            Borrar foto
                        </Link>

                        <Link
                            component="button"
                            onClick={handleEditClick}
                            underline="none"
                            color="primary"
                            sx={{
                                fontSize: '0.875rem',
                                fontWeight: 400
                            }}
                        >
                            Editar
                        </Link>
                    </Box>
                </Box>
            </Box>

            <UploadBox
                onClick={handleBoxClick}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <input
                    type="file"
                    accept="image/png, image/jpeg"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                />

                <FileUploadOutlinedIcon color="action" sx={{ fontSize: 32, mb: 2 }} />

                <Typography sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: 'primary.main' }}>
                        Haz click para subir
                    </Box>
                    {' o arrastra el archivo'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    PNG, JPG (Max. 800x400px)
                </Typography>
            </UploadBox>
        </Paper>
    );
};

export default ProfilePhoto;